import { adminApi } from "./AdminAuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export async function adminLogin({ email, password }) {
    try {
        const { data } = await adminApi.post("/login", { email, password });
        return { ok: true, data };
    } catch (err) {
        return {
            ok: false,
            rateLimited: err?.response?.status === 429,
            error:
                err?.response?.status === 401
                    ? "Invalid credentials"
                    : err?.response?.data?.message ?? "Sign in failed.",
        };
    }
}

export async function getStats(includeSeed = false) {
    const { data } = await adminApi.get("/stats", {
        params: { include_seed: includeSeed },
    });
    return data;
}

export async function getResponses(params) {
    const { data } = await adminApi.get("/responses", { params });
    return data;
}

export async function getResponseDetail(id) {
    const { data } = await adminApi.get(
        `/responses/${encodeURIComponent(id)}`,
    );
    return data;
}

export async function deleteResponse(id) {
    const { data } = await adminApi.delete(
        `/responses/${encodeURIComponent(id)}`,
    );
    return data;
}

export async function bulkDeleteResponses(ids) {
    try {
        const { data } = await adminApi.post("/responses/bulk-delete", { ids });
        return { ok: true, data };
    } catch (err) {
        return {
            ok: false,
            status: err?.response?.status,
            error:
                err?.response?.data?.detail ||
                err?.message ||
                "Bulk delete failed",
        };
    }
}

export async function getSeedCount() {
    const { data } = await adminApi.get("/seed-count");
    return data;
}

export async function wipeSeeds() {
    const { data } = await adminApi.post("/wipe-seeds");
    return data;
}

export async function reseedDemo() {
    try {
        const { data } = await adminApi.post("/reseed");
        return { ok: true, data };
    } catch (err) {
        return {
            ok: false,
            alreadyPresent: err?.response?.status === 409,
            data: err?.response?.data,
            error: err?.response?.data?.detail || err?.message || "Reseed failed",
        };
    }
}

export async function getTopReferrers(limit = 50, includeSeed = false) {
    const { data } = await adminApi.get("/top-referrers", {
        params: { limit, include_seed: includeSeed },
    });
    return data;
}

export function buildExportUrl(params = {}) {
    const url = new URL(`${BACKEND_URL}/api/admin/export.csv`);
    Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== "") {
            url.searchParams.append(k, String(v));
        }
    });
    return url.toString();
}

/**
 * Stream `POST /admin/analyze` SSE chunks, calling onText / onDone / onError.
 * Returns a function the caller can invoke to abort early.
 */
export function streamAnalysis({
    token,
    includeSeed = false,
    force = false,
    onText,
    onDone,
    onError,
}) {
    const controller = new AbortController();
    const url = new URL(`${BACKEND_URL}/api/admin/analyze`);
    url.searchParams.set("include_seed", String(includeSeed));
    if (force) url.searchParams.set("force", "true");

    (async () => {
        try {
            const res = await fetch(url.toString(), {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    Accept: "text/event-stream",
                },
                signal: controller.signal,
            });
            if (!res.ok) {
                onError?.(`HTTP ${res.status}`);
                return;
            }
            const reader = res.body.getReader();
            const decoder = new TextDecoder("utf-8");
            let buffer = "";
            // eslint-disable-next-line no-constant-condition
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                let nl;
                while ((nl = buffer.indexOf("\n\n")) !== -1) {
                    const event = buffer.slice(0, nl);
                    buffer = buffer.slice(nl + 2);
                    if (!event.startsWith("data:")) continue;
                    const json = event.replace(/^data:\s*/, "").trim();
                    if (!json) continue;
                    try {
                        const parsed = JSON.parse(json);
                        if (parsed.error) {
                            onError?.(parsed.error);
                            return;
                        }
                        if (parsed.text) {
                            onText?.(parsed.text);
                        }
                        if (parsed.done) {
                            onDone?.(parsed);
                            return;
                        }
                    } catch {
                        /* ignore malformed event */
                    }
                }
            }
            onDone?.({});
        } catch (err) {
            if (err.name !== "AbortError") {
                onError?.(err?.message ?? "stream failed");
            }
        }
    })();

    return () => controller.abort();
}
